import { useMemo } from 'react';
import type { ApprovalDocumentSummary, DocumentStatus } from '../../../../../types/approval';
import { WidgetCard } from '../../WidgetCard';
import { useWidgetActions } from '../../widget-context';
// 2026-09-16 — 메뉴 게이트. 옛 판에는 있었는데 V2 로 옮기며 빠졌다(아래 주석).
import { useMenuGate } from '../../../../../lib/menu-gate';
import { useWidgetItems } from '../../use-widget-items';
import { useDraftedApprovalsWhen, useIncomingApprovals } from '../../widget-queries';
import { mmdd } from './period';
import './widgets-v2.css';

/**
 * 전자결재 v2 — 보여 줄 문서 · 문서 상태 · 개수 · 줄에 보일 것 (2026-09-16 · 대시보드 시안 v2 V3a · 깃발 켤 때만).
 *
 * ★★★ 머리 칩은 **새로 달지 않는다**(W-Q3 — 옛 위젯에 칩이 없었다). 무엇을 볼지는 설정이 정한다.
 * ★★ 「지금 대기자 이름」은 목록 응답의 `waiting` 에 이미 있다 — 내 차례면 「내 차례」.
 *    「반려 사유」는 서버 칸이 없어(DR10) 설정에서 막혀 있고 여기서도 그리지 않는다.
 * ★ 문서 상태는 **화면에서 거른다** — 서버의 `status` 는 `view=involved` 전용이라 올린 문서 · 결재할 문서에 못 쓴다.
 * ★ 「내가 결재할 문서」만 볼 때는 올린 문서를 부르지 않는다(반대도 같다).
 */

const DOC_STATUS_META: Record<DocumentStatus, { label: string; cls: string }> = {
  DRAFT: { label: '임시저장', cls: '' },
  IN_PROGRESS: { label: '진행중', cls: 'dw-chip--new' },
  APPROVED: { label: '완료', cls: 'dw-chip--ok' },
  REJECTED: { label: '반려', cls: 'dw-chip--reject' },
  RECALLED: { label: '회수', cls: '' },
};

const EMPTY: Record<string, string> = {
  drafted: '상신한 문서가 없습니다', incoming: '결재할 문서가 없습니다', both: '문서가 없습니다',
};

function when(d: ApprovalDocumentSummary): number {
  return new Date(d.draftedAt ?? d.createdAt).getTime();
}

export function ApprovalWidgetV2() {
  const { go } = useWidgetActions();
  const items = useWidgetItems('approval');
  const view = (items.option<string>('view') ?? 'drafted') as 'drafted' | 'incoming' | 'both';
  const statuses = items.option<string[]>('status') ?? [];
  const count = Number(items.option<string>('count')) || 3;
  const showProgress = items.has('progress');
  const showWaiting = items.has('waiting');
  const showDrafted = items.has('draftedAt');

  /**
   * ★★★ **관리자가 전자결재 메뉴를 끄면 이 위젯도 멈춘다** (2026-09-16 고침).
   *
   *   옛 판(`WorkWidgets.tsx` `ApprovalWidgetLegacy`)은 `useMenuGate` 로 **안내를 그리고 조회도 막았다**.
   *   그 게이트는 「전자결재 위젯만 게이트가 없다」는 것을 보고 다른 위젯과 같게 맞춘 고침이었는데,
   *   **V2 로 옮기며 통째로 빠졌다** — 안내도 없고 끈 메뉴인데 **API 를 계속 불렀다**.
   * ★ 안내 · 「더보기」 숨김은 `WidgetCard` 가 `menuOff` 로 한다(아래). 여기서는 **조회를 막는다** —
   *   안내만 그리고 요청을 계속 보내면 절반만 고친 것이다.
   */
  const gate = useMenuGate('approval', '전자결재');
  const drafted = useDraftedApprovalsWhen(gate.enabled && view !== 'incoming');
  const incoming = useIncomingApprovals(gate.enabled && view !== 'drafted');

  const all = useMemo(() => {
    // ★ 둘 다 볼 때 같은 문서가 두 목록에 있을 수 있다 — 한 번만 세우되, 어느 쪽에만 있는 칸(`waiting`)도 잃지 않는다.
    const byId = new Map<string, ApprovalDocumentSummary>();
    for (const d of [
      ...(view !== 'incoming' ? drafted.data ?? [] : []),
      ...(view !== 'drafted' ? incoming.data ?? [] : []),
    ]) {
      const seen = byId.get(d.id);
      byId.set(d.id, seen ? { ...d, ...seen, waiting: seen.waiting ?? d.waiting } : d);
    }
    return [...byId.values()].sort((a, b) => when(b) - when(a));
  }, [drafted.data, incoming.data, view]);
  const rows = all.filter((d) => statuses.includes(d.status));

  const loading = (view !== 'incoming' && drafted.loading) || (view !== 'drafted' && incoming.loading);
  const error = (view !== 'incoming' ? drafted.error : null) ?? (view !== 'drafted' ? incoming.error : null);
  const target = view === 'incoming' ? '/approval/pending' : '/approval/drafts';

  return (
    <WidgetCard
      title="전자결재 진행현황"
      link={{ label: '더보기', onClick: () => go(target) }}
      loading={loading}
      error={error}
      empty={all.length === 0 ? EMPTY[view] : rows.length === 0 ? '고른 상태의 문서가 없습니다' : null}
      // 끈 메뉴면 안내 한 줄 + 「더보기」 숨김 — 다른 위젯과 같은 처리(WidgetCard 가 한다).
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, count).map((doc) => {
          const meta = DOC_STATUS_META[doc.status] ?? { label: doc.status, cls: '' };
          const total = Math.max(1, doc.totalSteps);
          const done = Math.min(doc.currentStep, total);
          const waiting = showWaiting && doc.status === 'IN_PROGRESS' && doc.waiting
            ? (doc.waiting.mine ? '내 차례' : doc.waiting.name ? `${doc.waiting.name} 차례` : null)
            : null;
          const draftedAt = showDrafted && doc.draftedAt ? mmdd(new Date(doc.draftedAt)) : null;
          const sub = [showProgress ? `결재선 ${done}/${total}` : null, waiting, draftedAt].filter(Boolean).join(' · ');
          return (
            <button type="button" key={doc.id} className="dw-appr" onClick={() => go(target)}>
              <span className="dw-appr__top">
                <span className="dw-appr__t" title={doc.title}>{doc.title}</span>
                <span className={`dw-chip ${meta.cls}`}>{meta.label}</span>
              </span>
              {showProgress && (
                <span className="dw-appr__line" aria-hidden>
                  {Array.from({ length: total }, (_, i) => (
                    <span key={i} className="dw-appr__step">
                      {i > 0 && <span className={`dw-appr__seg${i <= done ? ' is-done' : ''}`} />}
                      <span className={`dw-appr__node${i < done ? ' is-done' : ''}`} />
                    </span>
                  ))}
                </span>
              )}
              {sub && <span className="dw-appr__s">{sub}</span>}
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}
